import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ButtonModule, InputModule, RadioModule, SelectModule } from '@sag-ui';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AffectedItemsFormGroup, QualityInternationalFormGroup } from '../../../models/cod-create.model';
import { OrderLineTableComponent } from '../../../../shared/components/order-line-table/order-line-table.component';
import { CountrySpecificLabelIssue, IOrderLine, QualityIssueType } from '@cod-monorepo/interfaces';
import { LabelError } from '@cod-monorepo/interfaces';
import { tap } from 'rxjs';

@Component({
    selector: 'app-cod-affected-items-quality-step',
    templateUrl: './affected-items-quality.component.html',
    imports: [
        CommonModule,
        SelectModule,
        InputModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        OrderLineTableComponent,
        RadioModule
    ],
    standalone: true
})
export class CodAffectedItemsQualityInternationalStepComponent implements OnInit {
    @Input({ required: true })
    affectedItemsForm: FormGroup<AffectedItemsFormGroup>;
    @Input({ required: true })
    form: FormGroup<QualityInternationalFormGroup>;

    public issueTypes = Object.values(QualityIssueType);
    public labelErrors = LabelError;
    public countrySpecificLabelIssue = CountrySpecificLabelIssue;

    public showCountrySpecificLabel = false;
    public showLabelError = false;

    ngOnInit(): void {
        this.initTypeChangeListener();
        this.initCountrySpecificLabelMissingListener();
    }

    public get confirmed(): boolean {
        return this.affectedItemsForm.controls.confirmed.value ?? false;
    }

    public get lineItems(): IOrderLine[] {
        return this.affectedItemsForm.controls.affectedOrderLines.value ?? [];
    }

    public get disabled(): boolean {
        return this.affectedItemsForm.disabled;
    }

    public setConfirmed(): void {
        this.affectedItemsForm.controls.confirmed.setValue(true);
    }

    public get valid(): boolean {
        const orderLinesSelected = (this.affectedItemsForm.controls.affectedOrderLines.value ?? []).length > 0;
        const labelMissing = this.form.controls.countrySpecificLabelMissing.value;
        const labelErrorSelected = this.form.controls.labelError.value;

        switch (this.form.controls.issueType.value) {
            case QualityIssueType.COUNTRY_LABEL_ISSUE: {
                if (labelMissing === CountrySpecificLabelIssue.INCORRECT) {
                    return Boolean(labelErrorSelected) && orderLinesSelected;
                }

                return orderLinesSelected;
            }
            case QualityIssueType.PRODUCT_LABEL:
                return Boolean(labelErrorSelected) && orderLinesSelected;
            default:
                return false;
        }
    }

    public handleSelectionChange(value: IOrderLine[]): void {
        this.affectedItemsForm.controls.confirmed.setValue(false);
        this.affectedItemsForm.get('affectedOrderLines')?.setValue(value);
    }

    private initTypeChangeListener(): void {
        this.form.controls.issueType.valueChanges
            .pipe(
                tap(value => {
                    this.affectedItemsForm.controls.typeValid.setValue(!!value);
                    this.form.controls.labelError.setValue(null);
                    this.form.controls.countrySpecificLabelMissing.setValue(null);

                    switch (value) {
                        case QualityIssueType.COUNTRY_LABEL_ISSUE:
                            this.showCountrySpecificLabel = true;
                            this.showLabelError = false;
                            break;
                        case QualityIssueType.PRODUCT_LABEL:
                            this.showLabelError = true;
                            this.showCountrySpecificLabel = false;
                            break;
                        default:
                            this.showCountrySpecificLabel = false;
                            this.showLabelError = false;
                            break;
                    }
                })
            )
            .subscribe();
    }

    private initCountrySpecificLabelMissingListener(): void {
        this.form.controls.countrySpecificLabelMissing.valueChanges
            .pipe(
                tap(value => {
                    this.showLabelError = value === CountrySpecificLabelIssue.INCORRECT;
                })
            )
            .subscribe();
    }
}
